import { syntaxTree } from "@codemirror/language";
import { type Diagnostic, linter, setDiagnostics } from "@codemirror/lint";
import type { EditorState, Text } from "@codemirror/state";
import type { EditorView } from "@codemirror/view";
import type { SyntaxNode } from "@lezer/common";
import type { PlaygroundValidateResponse, ScriptDiagnostic } from "@/types/api";
import { RHAI_KNOWN_FUNCTIONS, scopeDiagnostics } from "./rhai/rhaiScope";
import { dryRunPayload, isRunnablePayload } from "./rhaiDryRun";
import { treeSyntaxErrors } from "./treeSyntaxErrors";

// Live Rhai linting for the transform editor (TTTL-225), in three passes
// over one document:
//  1. local — the Lezer tree (rhai/rhai.grammar) gives syntax errors, and
//     on a clean parse the scope pass (rhai/rhaiScope) gives undefined
//     variables and unknown functions. Synchronous, published at once.
//  2. engine — POST /transform/validate, the same compile step a deployed
//     pipeline runs (plus the transform(data, attributes) rule). Its parse
//     verdict outranks the local grammar's.
//  3. dry run — on a clean script with a runnable input document,
//     POST /transform/run lifts the engine's runtime error (rhaiDryRun.ts),
//     which catches what the sample document exercises.
// The engine answers with 1-based line/column diagnostics; toDiagnostics
// turns them into the CodeMirror Diagnostics the shared squiggles, gutter
// dots and Errors popover already know.

/** The idle delay before a lint pass: the local pass is instant, the
 *  engine passes are round trips. */
export const RHAI_LINT_DELAY_MS = 300;

export type ValidateScript = (
  script: string,
) => Promise<PlaygroundValidateResponse>;

/** The runtime pass: the script's dry run against the input document,
 *  answering with the engine's runtime verdict (rhaiDryRun.dryRunScript)
 *  — or throwing when no verdict could be had. */
export type DryRunScript = (
  script: string,
  payload: string,
) => Promise<ScriptDiagnostic[]>;

/** The lint config's refresh rule: a new input document re-lints the
 *  script, since the runtime verdict depends on it. */
export function payloadChanged(update: {
  state: EditorState;
  startState: EditorState;
}): boolean {
  return (
    update.state.facet(dryRunPayload) !== update.startState.facet(dryRunPayload)
  );
}

// The document offset of the engine's 1-based line and column, clamped to
// the document: a line past the end lands on the last line, a column past
// the line's end on its end.
function offsetAt(doc: Text, line: number, column: number): number {
  const lineNumber = Math.min(Math.max(line, 1), doc.lines);
  const docLine = doc.line(lineNumber);
  return Math.min(docLine.from + Math.max(column, 1) - 1, docLine.to);
}

/** The engine's diagnostics as CodeMirror ranges over `doc`. The engine
 *  reports most parse errors as a point (end == start): the squiggle then
 *  runs from that point to the end of its line, so the Errors row keeps
 *  the engine's column while the underline stays visible. A point at the
 *  line's end (or past the document) takes the whole line instead — the
 *  JSON editors treat their parser's zero-width ranges the same way. */
export function toDiagnostics(
  doc: Text,
  diagnostics: readonly ScriptDiagnostic[],
): Diagnostic[] {
  return diagnostics.map((d) => {
    let from = offsetAt(doc, d.line, d.column);
    let to = offsetAt(doc, d.end_line, d.end_column);
    if (to <= from) {
      const line = doc.lineAt(from);
      to = line.to;
      if (to === from) from = line.from;
    }
    return { from, to, severity: d.severity, message: d.message };
  });
}

export interface LocalLint {
  syntax: Diagnostic[];
  scope: Diagnostic[];
}

/** The engine's rule for a transform script, in its words. */
export const TRANSFORM_SIGNATURE_MESSAGE =
  "Script must define a 'transform(data, attributes)' function with exactly 2 parameters";

// The engine (validate.rs) refuses a script without a two-parameter
// `transform` — reported on line 1, where it reports it.
function declaresTransform(state: EditorState, fn: SyntaxNode): boolean {
  const name = fn.getChild("FunctionName");
  const params =
    fn.getChild("ParamList")?.getChildren("VariableDefinition") ?? [];
  return (
    name !== null &&
    state.sliceDoc(name.from, name.to) === "transform" &&
    params.length === 2
  );
}

function hasTransformFunction(state: EditorState): boolean {
  let found = false;
  syntaxTree(state).iterate({
    enter: (node) => {
      if (
        node.name === "FunctionDeclaration" &&
        declaresTransform(state, node.node)
      )
        found = true;
    },
  });
  return found;
}

function transformSignatureError(state: EditorState): Diagnostic[] {
  if (hasTransformFunction(state)) return [];
  const line = state.doc.line(1);
  return [
    {
      from: line.from,
      to: line.to,
      severity: "error",
      message: TRANSFORM_SIGNATURE_MESSAGE,
    },
  ];
}

/** The offline pass: syntax errors from the tree, and the scope pass over
 *  it — the tree is error-tolerant, so the intact statements of a broken
 *  script still get checked (rhaiScope skips the broken ones) — plus the
 *  engine's transform-signature rule. Synchronous; the tree is already
 *  there for highlighting. */
export function localDiagnostics(state: EditorState): LocalLint {
  const syntax = treeSyntaxErrors(state);
  const signature = syntax.length === 0 ? transformSignatureError(state) : [];
  const scope = scopeDiagnostics(
    syntaxTree(state),
    state.doc.toString(),
    RHAI_KNOWN_FUNCTIONS,
  ).map((d): Diagnostic => ({ ...d, severity: "error" }));
  return { syntax, scope: [...signature, ...scope] };
}

// The engine stops at its first syntax error; local errors on other lines
// are the ones it has not reached yet.
function beyondEngine(
  doc: Text,
  local: Diagnostic[],
  engine: Diagnostic[],
): Diagnostic[] {
  const lines = new Set(engine.map((d) => doc.lineAt(d.from).number));
  return local.filter((d) => !lines.has(doc.lineAt(d.from).number));
}

/** What the editor shows once every pass has answered. The engine's parse
 *  verdict outranks the local grammar's: a clean compile retires every
 *  local syntax error (a grammar gap must never block the tick); when the
 *  engine does report one, its wording wins for that line and the local
 *  errors on other lines stay — the engine stops at the first. `engine`
 *  is null when validate could not be reached. A runtime (dry-run) error
 *  at a position the scope pass already covers is the same finding, kept
 *  once. */
export function mergeVerdicts(
  doc: Text,
  local: LocalLint,
  engine: Diagnostic[] | null,
  runtime: Diagnostic[],
): Diagnostic[] {
  let syntax: Diagnostic[];
  if (engine === null) syntax = local.syntax;
  else if (engine.length === 0) syntax = [];
  else syntax = [...engine, ...beyondEngine(doc, local.syntax, engine)];
  // A rule both sides know (the transform signature) shows once, in the
  // engine's row.
  const said = new Set(syntax.map((d) => d.message));
  const scope = local.scope.filter((d) => !said.has(d.message));
  const covered = new Set(scope.map((d) => d.from));
  const extra = runtime.filter((d) => !covered.has(d.from));
  return [...syntax, ...scope, ...extra].sort((a, b) => a.from - b.from);
}

// The runtime pass needs a runnable input document (the dryRunPayload
// facet). A dry run that could not be had — engine outage, network —
// leaves the other verdicts standing rather than turning them into
// silence.
async function runtimeDiagnostics(
  dryRun: DryRunScript | undefined,
  state: EditorState,
  text: string,
): Promise<Diagnostic[]> {
  if (!dryRun) return [];
  const payload = state.facet(dryRunPayload);
  if (!isRunnablePayload(payload)) return [];
  try {
    return toDiagnostics(state.doc, await dryRun(text, payload));
  } catch {
    return [];
  }
}

/** The lint source: publishes the local pass at once, then validates with
 *  the engine, dry-runs a script every pass found clean, and reports the
 *  merged result through `onDiagnostics` as well, for the status bar's
 *  count and rows. An empty document is never sent (and reads as clean);
 *  a result for a document that changed meanwhile is dropped — the lint
 *  package re-runs on the new text on its own. */
// The engine's parse verdict, or null when validate could not be reached.
async function engineDiagnostics(
  validate: ValidateScript,
  state: EditorState,
  text: string,
): Promise<Diagnostic[] | null> {
  try {
    const response = await validate(text);
    return toDiagnostics(state.doc, response.diagnostics ?? []);
  } catch {
    return null;
  }
}

// Nothing found by the local pass nor the engine — the dry run's precondition.
function allClean(local: LocalLint, engine: Diagnostic[] | null): boolean {
  return (
    local.syntax.length === 0 &&
    local.scope.length === 0 &&
    (engine?.length ?? 0) === 0
  );
}

export function rhaiLintSource(
  validate: ValidateScript,
  onDiagnostics?: (diagnostics: Diagnostic[], state: EditorState) => void,
  dryRun?: DryRunScript,
): (view: EditorView) => Promise<Diagnostic[]> {
  return async (view) => {
    const state = view.state;
    const text = state.doc.toString();
    if (text.trim() === "") {
      onDiagnostics?.([], state);
      return [];
    }
    const local = localDiagnostics(state);
    view.dispatch(setDiagnostics(state, [...local.syntax, ...local.scope]));
    const engine = await engineDiagnostics(validate, state, text);
    const runtime = allClean(local, engine)
      ? await runtimeDiagnostics(dryRun, state, text)
      : [];
    if (!view.state.doc.eq(state.doc)) return [];
    const diagnostics = mergeVerdicts(state.doc, local, engine, runtime);
    onDiagnostics?.(diagnostics, state);
    return diagnostics;
  };
}

/** The transform editor's linter extension. */
export function rhaiLinter(
  validate: ValidateScript,
  onDiagnostics?: (diagnostics: Diagnostic[], state: EditorState) => void,
  dryRun?: DryRunScript,
) {
  return linter(rhaiLintSource(validate, onDiagnostics, dryRun), {
    delay: RHAI_LINT_DELAY_MS,
    needsRefresh: payloadChanged,
  });
}
